import { ChartData } from 'chart.js';
import { cn } from 'cn';

import { toCamelCase } from '@vestigas/utils';

/**
 * Get the colors and labels of the datasets from the chart data.
 * @param data - The full chart data.
 * @param labelTranslationsMap - An object with the translations for the labels.
 * @returns The colors and labels of the datasets.
 */
export function getLegendData(
  data: ChartData,
  labelTranslationsMap?: Record<string, string>,
) {
  return (
    data?.datasets
      ?.map((dataset) => {
        const backgroundColor = [dataset.backgroundColor].flat().at(0);
        const label =
          labelTranslationsMap?.[toCamelCase(dataset.label ?? '')] ||
          dataset.label;

        if (!backgroundColor || !label) {
          return null;
        }

        return {
          color: backgroundColor,
          label,
        };
      })
      ?.filter(Boolean) ?? []
  );
}

const LegendItem = ({
  color,
  label,
}: {
  readonly color: string;
  readonly label: string;
}) => (
  <div className="flex items-center gap-1.5">
    <div
      className="size-3 flex-none rounded-xs"
      style={{ backgroundColor: color }}
    />
    <div className="min-w-0 flex-1 text-start text-xs leading-tight text-tiga-text-lighter">
      <div className="truncate">{label}</div>
    </div>
  </div>
);

type P = {
  readonly data: Array<{
    color: string;
    label: string;
  }>;
} & ComponentStyling;

/**
 * ChartLegend
 *
 * Displays a legend of the displayed datasets for a chart.
 */
export const ChartLegend = ({ className, data, style }: P) => {
  if (!data?.length) {
    return null;
  }

  return (
    <div className={cn('flex justify-end gap-4', className)} style={style}>
      {data.map(({ color, label }) => (
        <LegendItem key={label} color={color} label={label} />
      ))}
    </div>
  );
};
